import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { CalendarIcon, Check } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { visitTypes, timeSlots } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingModal = ({ isOpen, onClose }) => {
  const [date, setDate] = useState();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    visitType: '',
    timeSlot: '',
    groupSize: '1',
    specialRequests: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error('Please select a date for your visit');
      return;
    }

    if (!formData.visitType) {
      toast.error('Please select a visit type');
      return;
    }

    if (!formData.timeSlot) {
      toast.error('Please select a time slot');
      return;
    }

    setIsSubmitting(true);

    try {
      const bookingData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        visit_type: formData.visitType,
        date: format(date, 'yyyy-MM-dd'),
        time_slot: formData.timeSlot,
        group_size: parseInt(formData.groupSize, 10),
        special_requests: formData.specialRequests || null
      };

      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Booking failed');
      }

      const result = await response.json();
      
      toast.success('Booking Submitted!', {
        description: `Your visit request for ${format(date, 'MMMM d, yyyy')} at ${formData.timeSlot} has been received. We'll confirm within 24 hours.`
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        visitType: '',
        timeSlot: '',
        groupSize: '1',
        specialRequests: ''
      });
      setDate(undefined);
      onClose();
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Booking Failed', {
        description: error.message || 'Something went wrong. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold" style={{ fontFamily: "'Crimson Text', serif" }}>
            Book Your Visit
          </DialogTitle>
          <DialogDescription>
            Schedule your experience at Agreenerside. We'll confirm your booking within 24 hours.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Personal Information */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input 
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Your full name"
                required
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input 
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input 
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(123) 456-7890"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Visit Details */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="visitType">Visit Type *</Label>
              <Select value={formData.visitType} onValueChange={(value) => handleChange('visitType', value)} required>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select your experience" />
                </SelectTrigger>
                <SelectContent>
                  {visitTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.label} ({type.duration})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Visit Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal mt-1"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, 'PPP') : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(date) => date < new Date() || date < new Date("1900-01-01")}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label htmlFor="timeSlot">Preferred Time *</Label>
                <Select value={formData.timeSlot} onValueChange={(value) => handleChange('timeSlot', value)} required>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        {slot}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="groupSize">Group Size</Label>
              <Input 
                id="groupSize"
                type="number"
                min="1"
                max="20"
                value={formData.groupSize}
                onChange={(e) => handleChange('groupSize', e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="specialRequests">Special Requests or Questions</Label>
              <Textarea 
                id="specialRequests"
                value={formData.specialRequests}
                onChange={(e) => handleChange('specialRequests', e.target.value)}
                placeholder="Any specific interests, dietary requirements, or accessibility needs?"
                rows={4}
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="flex-1 bg-black text-white hover:bg-gray-800 transition-all duration-200"
            >
              {isSubmitting ? 'Submitting...' : 'Confirm Booking'}
              {!isSubmitting && <Check className="ml-2" size={18} />}
            </Button>
            <Button 
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
